'use client'

import { Loader2Icon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import StepUpVerificationFields, {
  StepUpFieldValues,
} from '@/components/dialog/settings/step-up-verification-fields'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useAuth } from '@/features/auth/hooks'
import {
  creditPresets,
  type CreditSettings,
  isSellableCredits,
  needsStepUp,
  routeSaveFailure,
  validateSettings,
} from '@/features/settings/billing/credits/lib/credits'
import { toProtoSettings } from '@/features/settings/billing/credits/lib/mappers'
import { useCreditFormat } from '@/features/settings/billing/credits/use-credit-format'
import { useNumberFormat, useNumberInput } from '@/hooks/use-number-format'
import { classifyStepUpError, getStepUpMode, pickStepUpVerification } from '@/lib/step-up'
import { toast } from '@/lib/toast'
import { useApiBilling, useUpdateApiBillingSettings } from '@/service/api-billing.hook'
import { useGet2FABackupCodes } from '@/service/user-profile.hook'
import { SensitiveAction } from '@/types/generated/common/auth'
import type { ErrorResponse } from '@/types/service'

type Step = 'form' | 'verify'

type VerifyFormData = {
  currentPassword: string
  totpCode?: string
  backupCode?: string
  emailCode?: string
} & StepUpFieldValues

const EMPTY_VERIFY: VerifyFormData = {
  currentPassword: '',
  totpCode: '',
  backupCode: '',
  emailCode: '',
}

/** The editable fields as typed; numbers stay text until they're read into a draft. */
interface FormFields {
  noLimit: boolean
  limit: string
  refillEnabled: boolean
  refillCredits: string
  threshold: string
}

function toFields(settings: CreditSettings): FormFields {
  return {
    noLimit: settings.spendLimitWeekly === undefined,
    limit: settings.spendLimitWeekly === undefined ? '' : String(settings.spendLimitWeekly),
    refillEnabled: settings.refillEnabled,
    refillCredits: settings.refillCredits ? String(settings.refillCredits) : '',
    threshold: settings.refillThreshold ? String(settings.refillThreshold) : '',
  }
}

/** An empty or unparsable field reads as 0, which validation then names. */
function toDraft(fields: FormFields): CreditSettings {
  return {
    spendLimitWeekly: fields.noLimit ? undefined : Number(fields.limit) || 0,
    refillEnabled: fields.refillEnabled,
    refillThreshold: Number(fields.threshold) || 0,
    refillCredits: Number(fields.refillCredits) || 0,
  }
}

/**
 * Weekly limit + auto refill, sent whole. The step-up is asked for only when the change could
 * spend more (see `needsStepUp`), judged against settings fetched as the dialog opens — the
 * server compares against what it has stored, so a stale copy would mispredict.
 */
export default function EditCreditLimitsDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const t = useTranslations('settings.billing.credits.edit')
  const fmt = useNumberFormat()
  const { cost } = useCreditFormat()
  const { hasPassword } = useAuth()
  const { data: billing, refetch } = useApiBilling()
  const update = useUpdateApiBillingSettings()
  const { data: backupCodes, isLoading: is2faLoadingRaw } = useGet2FABackupCodes(
    {},
    { treatMissingAsDisabled: true }
  )
  const is2faEnabled = (backupCodes?.backupCodes.length ?? 0) > 0
  const is2faLoading = is2faLoadingRaw && !backupCodes

  const [step, setStep] = useState<Step>('form')
  /** The stored settings this edit started from; null until the on-open fetch lands. */
  const [base, setBase] = useState<CreditSettings | null>(null)
  const [fields, setFields] = useState<FormFields | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [factorOverride, setFactorOverride] = useState<'totp' | 'email' | null>(null)
  const totpFactor = factorOverride ? factorOverride === 'totp' : is2faEnabled

  const form = useForm<VerifyFormData>({ defaultValues: EMPTY_VERIFY })
  const limitInput = useNumberInput(fields?.limit ?? '', { integer: true })
  const thresholdInput = useNumberInput(fields?.threshold ?? '', { integer: true })
  const refillCreditsInput = useNumberInput(fields?.refillCredits ?? '', { integer: true })

  useEffect(() => {
    if (!open) return
    let live = true
    void refetch().then(({ data }) => {
      if (!live || !data) return
      setBase(data.settings)
      setFields(toFields(data.settings))
    })
    return () => {
      live = false
    }
  }, [open, refetch])

  const creditPrice = billing?.creditPrice
  const hasCard = Boolean(billing?.cardLast4)
  const draft = fields ? toDraft(fields) : null
  const errors = draft ? validateSettings(draft, creditPrice, hasCard) : {}
  const hasErrors = Object.keys(errors).length > 0

  const mode = getStepUpMode(totpFactor)
  const verificationReady =
    (!hasPassword || Boolean(form.watch('currentPassword'))) &&
    (mode === 'totp'
      ? Boolean(form.watch('totpCode')) || Boolean(form.watch('backupCode'))
      : form.watch('emailCode')?.length === 6)

  const patch = (next: Partial<FormFields>) =>
    setFields((prev) => (prev ? { ...prev, ...next } : prev))

  const reset = () => {
    setStep('form')
    setBase(null)
    setFields(null)
    setFormError(null)
    setFactorOverride(null)
    form.reset(EMPTY_VERIFY)
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) reset()
    onOpenChange(next)
  }

  const save = async (verification?: VerifyFormData) => {
    if (!draft) return
    setFormError(null)
    form.clearErrors()
    try {
      await update.mutateAsync({
        settings: toProtoSettings(draft),
        currentPassword: verification?.currentPassword ?? '',
        ...(verification ? pickStepUpVerification(verification) : {}),
      })
      toast.success(t('saved'))
      handleOpenChange(false)
    } catch (error) {
      // Never retried here: every refused attempt counts toward the step-up throttle.
      const route = routeSaveFailure(
        classifyStepUpError((error as ErrorResponse)?.message),
        step === 'verify'
      )
      switch (route.action) {
        case 'verify':
          if (route.factor) {
            setFactorOverride(route.factor)
            setFormError(
              route.factor === 'totp' ? t('errors.totpRequired') : t('errors.emailCodeRequired')
            )
          }
          setStep('verify')
          break
        case 'fieldError':
          if (route.field === 'currentPassword') {
            form.setError('currentPassword', { message: t('errors.password') })
          } else if (route.field === 'totpCode') {
            const field =
              verification?.backupCode && !verification.totpCode ? 'backupCode' : 'totpCode'
            form.setError(field, { message: t('errors.totpInvalid') })
          } else {
            form.setError('emailCode', { message: t('errors.emailCodeInvalid') })
          }
          break
        case 'conflict': {
          // Someone else saved first. Show the form again against the new stored settings,
          // keeping what the user typed, so the step-up is judged afresh.
          const { data } = await refetch()
          if (data) setBase(data.settings)
          form.reset(EMPTY_VERIFY)
          setStep('form')
          setFormError(t('conflict'))
          break
        }
        case 'message':
          setFormError(route.message || t('errors.failed'))
      }
    }
  }

  const onContinue = () => {
    if (!draft || !base || hasErrors) return
    if (needsStepUp(base, draft)) {
      setFormError(null)
      setStep('verify')
    } else {
      void save()
    }
  }

  const fieldError = (key: keyof typeof errors) =>
    errors[key] ? t(`errors.${errors[key]}`) : undefined
  const refillSellable =
    creditPrice !== undefined && !!draft && isSellableCredits(creditPrice, draft.refillCredits)
  const refillCreditsError =
    errors.refillCredits === 'refillCreditsRange' && creditPrice
      ? t('errors.refillCreditsRange', {
          min: fmt.number(creditPrice.minCredits),
          max: fmt.number(creditPrice.maxCredits),
        })
      : fieldError('refillCredits')

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-32px)] overflow-y-auto">
        <DialogTitle className="flex flex-col gap-2">
          {t('title')}
          <p className="text-foreground/60 text-sm font-normal tracking-tight">
            {step === 'form' ? t('description') : t('verifyDescription')}
          </p>
        </DialogTitle>

        {!fields || !draft ? (
          <div className="flex justify-center py-10">
            <Loader2Icon className="text-foreground/50 size-5 animate-spin" />
          </div>
        ) : step === 'form' ? (
          <form
            className="mt-4 flex w-full flex-col gap-5"
            onSubmit={(event) => {
              event.preventDefault()
              onContinue()
            }}
          >
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-4">
                <Label className="text-sm tracking-tight">{t('limit')}</Label>
                <label className="text-foreground/70 flex cursor-pointer items-center gap-2 text-xs">
                  {t('noLimitSwitch')}
                  <Switch
                    checked={fields.noLimit}
                    onCheckedChange={(noLimit) => patch({ noLimit })}
                  />
                </label>
              </div>
              {!fields.noLimit && (
                <Input
                  {...limitInput.bind((limit) => patch({ limit }))}
                  error={fieldError('limit')}
                />
              )}
              {fields.noLimit && errors.limit && (
                <p className="text-destructive text-xs">{fieldError('limit')}</p>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-4">
                <span className="flex flex-col">
                  <Label className="text-sm tracking-tight">{t('refill')}</Label>
                  {!hasCard && (
                    <span className="text-foreground/50 text-xs tracking-tight">
                      {t('refillNoCard')}
                    </span>
                  )}
                </span>
                <Switch
                  checked={fields.refillEnabled}
                  // Turning refill off is always allowed; turning it on needs a saved card.
                  disabled={!fields.refillEnabled && !hasCard}
                  onCheckedChange={(refillEnabled) =>
                    patch({
                      refillEnabled,
                      // Start from the smallest preset so the field is never blank on first use.
                      refillCredits:
                        refillEnabled && !fields.refillCredits && creditPrice
                          ? String(creditPresets(creditPrice)[0]?.credits ?? creditPrice.minCredits)
                          : fields.refillCredits,
                    })
                  }
                />
              </div>
              {errors.refill && <p className="text-destructive text-xs">{fieldError('refill')}</p>}

              {fields.refillEnabled && (
                <>
                  <div className="flex flex-col gap-1">
                    <Label className="text-sm tracking-tight">{t('refillCredits')}</Label>
                    <Input
                      {...refillCreditsInput.bind((refillCredits) => patch({ refillCredits }))}
                      disabled={!creditPrice}
                      error={refillCreditsError}
                    />
                    {refillSellable && (
                      <span className="text-foreground/50 text-xs tabular-nums">
                        {t('refillCost', { total: cost(creditPrice, draft.refillCredits) })}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-sm tracking-tight">{t('threshold')}</Label>
                    <Input
                      {...thresholdInput.bind((threshold) => patch({ threshold }))}
                      error={
                        errors.threshold && refillSellable
                          ? t('errors.thresholdRange', { max: fmt.number(draft.refillCredits) })
                          : undefined
                      }
                    />
                    {refillSellable && !errors.threshold && (
                      <span className="text-foreground/50 text-xs">
                        {t('thresholdHint', { max: fmt.number(draft.refillCredits) })}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>

            {formError && <p className="text-destructive text-sm">{formError}</p>}

            <Button
              type="submit"
              className="w-full"
              disabled={!base || hasErrors || is2faLoading || update.isPending}
            >
              {update.isPending ? <Loader2Icon className="size-3.5 animate-spin" /> : t('save')}
            </Button>
          </form>
        ) : (
          <form
            onSubmit={form.handleSubmit((data) => save(data))}
            className="mt-4 flex w-full flex-col gap-4"
            data-1p-ignore={false}
          >
            <StepUpVerificationFields
              form={form}
              hasPassword={hasPassword}
              totpEnabled={totpFactor}
              action={SensitiveAction.SENSITIVE_ACTION_UPDATE_API_BILLING}
            />
            {formError && <p className="text-destructive text-sm">{formError}</p>}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                disabled={update.isPending}
                onClick={() => {
                  setFormError(null)
                  setStep('form')
                }}
              >
                {t('back')}
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={update.isPending || !verificationReady}
              >
                {update.isPending ? <Loader2Icon className="size-3.5 animate-spin" /> : t('save')}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
